// frontend/src/types/project.types.ts

import { SkillLevel } from './courses.types';

export interface Project {
  id: string;
  title: string;
  description: string | null;
  difficulty: SkillLevel;
  starter_code: string | null;
  course_id: string | null;
  category?: string;
  is_published?: boolean;
  is_premium?: boolean;
  xp_reward?: number;
  estimated_duration?: string;
}

export type ProjectStatus = 'not_started' | 'in_progress' | 'completed';

export interface ProjectWithStatus extends Project {
  status: ProjectStatus;
  bestScore: number | null;
}

export interface SubmissionSummary {
  id: string;
  project_id: string;
  ai_score: number | null;
  ai_strengths: string[];
  ai_improvements: string[];
  ai_feedback: string | null;
  created_at: string;
}

export interface EvaluationCriteriaLabels {
  quality: string;
  logic: string;
  readability: string;
}

export interface ProjectEvaluation {
  quality: number;
  logic: number;
  readability: number;
  criteriaLabels?: EvaluationCriteriaLabels;
  improvements: string[];
  overallFeedback: string;
}

export interface SubmitProjectResponse {
  submissionId: string;
  xpAwarded: number;
  evaluation: ProjectEvaluation;
}

export interface PersonalizedProjectResult {
  id: string;
  title: string;
  description: string;
  difficulty: string;
  starter_code: string;
  category?: string;
}

const PROJECT_ICONS: Record<string, string> = {
  'Sayı Tahmin Oyunu': '🎮',
  'Hesap Makinesi': '🧮',
  'Görev Takip Uygulaması': '📋',
  'Basketbol Skor Takibi': '🏀',
  'Futbol Maçı Skor Takip Uygulaması': '⚽',
  'Futbol Penaltı Atışı Oyunu': '⚽',
  'Geometrik Şekiller Alan & Çevre Simülatörü': '📐',
  'İstatistik ve Veri Grafiği Analizi': '📊',
  'Güneş Sistemi Gezegen Yörünge Modeli': '🪐',
  'Fotosentez ve İklim Simülatörü': '🧪',
  'English Vocabulary Flashcard App': '🇬🇧',
  'Paragraf Ana Fikir & Kelime Analizi': '📚',
};

export function getProjectIcon(title: string): string {
  return PROJECT_ICONS[title] ?? '🎯';
}

export function getProjectCategory(project: { title: string; category?: string }): { label: string; icon: string; key: string } {
  const titleLower = project.title.toLowerCase();
  const cat = project.category?.toLowerCase() || '';

  if (cat.includes('math') || cat.includes('matematik') || titleLower.includes('geometri') || titleLower.includes('hesap') || titleLower.includes('istatistik')) {
    return { label: 'Matematik & Mantık', icon: '📐', key: 'math' };
  }
  if (cat.includes('science') || cat.includes('fen') || titleLower.includes('güneş') || titleLower.includes('fotosentez') || titleLower.includes('uzay')) {
    return { label: 'Fen & Teknoloji', icon: '🧪', key: 'science' };
  }
  if (cat.includes('english') || cat.includes('ingilizce') || titleLower.includes('english') || titleLower.includes('vocabulary')) {
    return { label: 'İngilizce', icon: '🇬🇧', key: 'english' };
  }
  if (cat.includes('turkish') || cat.includes('türkçe') || titleLower.includes('paragraf') || titleLower.includes('kelime') || titleLower.includes('hikaye')) {
    return { label: 'Türkçe & Edebiyat', icon: '📚', key: 'turkish' };
  }
  return { label: 'Kodlama & Algoritma', icon: '🐍', key: 'coding' };
}
